import React, { useEffect, useState } from 'react'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Breadcrumbs,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import SearchIcon from '../../assets/icons/SearchIcon'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ShortcutRenderer from './ShortcutRenderer'
import IndicatorIcon from '../../assets/icons/indicatorIcon'
import AddAlertIcon from '../../assets/icons/AddAlertIcon'
import Calender from '../../assets/icons/Calender'
import AddSymbol from '../../assets/icons/AddSymbol'
import Chart from '../../assets/icons/Chart'
import Trade from '../../assets/icons/Trade'
import AlarmIcon from '../../assets/icons/AlarmIcon'
import ArrowBackIcon from '../../assets/icons/ArrowBackIcon'
import Screener from '../../assets/icons/Screener'
import FullScreenIcon from '../../assets/icons/FullScreenIcon'

interface KeyboardShortcutsCategoryViewProps {
    onBackClick: Function;
}

const styles = {
    searchInput: {
        paddingX: '8px',
        borderRadius: '8px',
        border: '1px solid'
    }
}

type ShortcutCategory = 'Chart' | 'Trading' | 'Alerts' | 'Screener';

interface ShortcutData {
    icon: any;
    title: string;
    category: string;
    shortcutName: string;
}

interface CategoryData {
    categoryIcon: any;
    shortcuts: ShortcutData[];
}

const shortcutsData: Record<ShortcutCategory, CategoryData> = {
    Chart: {
        categoryIcon: <Chart sx={{ fontSize: '40px' }} />,
        shortcuts: [
            {
                icon: <IndicatorIcon />,
                title: 'Open indicators',
                category: 'Chart',
                shortcutName: 'openIndicatorDialog'
            },
            {
                icon: undefined,
                title: 'Load Chart Layout',
                category: 'Chart',
                shortcutName: 'openMultiPaneMenu'
            },
            {
                icon: undefined,
                title: 'Save Chart Layout',
                category: 'Chart',
                shortcutName: 'saveLayout'
            },
            {
                icon: undefined,
                title: 'Change Interval',
                category: 'Chart',
                shortcutName: 'openCustomTimeBucketIntervalDialog'
            },
            {
                icon: undefined,
                title: 'Replay open/close',
                category: 'Chart',
                shortcutName: 'openCloseChartReplayDialog'
            },
            {
                icon: <AddAlertIcon />,
                title: 'Add alert',
                category: 'Chart',
                shortcutName: 'openAlertDialog'
            },
            {
                icon: <Calender fontSize={'small'} />,
                title: 'Go to date',
                category: 'Chart',
                shortcutName: 'openChartDuration'
            },
            {
                icon: undefined,
                title: 'Reset chart view',
                category: 'Chart',
                shortcutName: 'resetChartView'
            },
            {
                icon: <AddSymbol fontSize={'small'} />,
                title: 'Add symbol to active watchlist',
                category: 'Chart',
                shortcutName: 'addToWatchlist'
            },
            {
                icon: <FullScreenIcon />,
                title: 'Fullscreen mode',
                category: 'Chart',
                shortcutName: 'showHideFullScreen'
            },
            {
                icon: undefined,
                title: 'Change chart type',
                category: 'Chart',
                shortcutName: 'openChartTypeMenu'
            },
            {
                icon: undefined,
                title: 'Zoom in',
                category: 'Chart',
                shortcutName: 'zoomIn'
            },
            {
                icon: undefined,
                title: 'Zoom out',
                category: 'Chart',
                shortcutName: 'zoomOut'
            },
            {
                icon: undefined,
                title: 'Move chart 1 bar to the left',
                category: 'Chart',
                shortcutName: 'moveChartLeft'
            },
            {
                icon: undefined,
                title: 'Move chart 1 bar to the right',
                category: 'Chart',
                shortcutName: 'moveChartRight'
            },
            {
                icon: undefined,
                title: 'Split horizontal',
                category: 'Chart',
                shortcutName: 'splitHorizontal'
            },
            {
                icon: undefined,
                title: 'Split vertical',
                category: 'Chart',
                shortcutName: 'splitVertical'
            },
            {
                icon: undefined,
                title: 'Delete current widget',
                category: 'Chart',
                shortcutName: 'deleteCurrentWidget'
            },
            {
                icon: undefined,
                title: 'Show Plus button on cursor',
                category: 'Chart',
                shortcutName: 'showPlusButtonOnCursor'
            },
            {
                icon: undefined,
                title: 'Open layouts save-menu',
                category: 'SAVE_MENU',
                shortcutName: 'openSaveMenu'
            }
        ]
    },
    Trading: {
        categoryIcon: <Trade />,
        shortcuts: [
            {
                icon: undefined,
                title: 'Open/close Trade Panel',
                category: 'Trading',
                shortcutName: 'openCloseTradePanel'
            },
            {
                icon: undefined,
                title: 'Place market order to buy',
                category: 'Trading',
                shortcutName: 'placeMarketOrderBuy'
            },
            {
                icon: undefined,
                title: 'Place market order to sell',
                category: 'Trading',
                shortcutName: 'placeMarketOrderSell'
            },
            {
                icon: undefined,
                title: 'Place limit order to buy',
                category: 'Trading',
                shortcutName: 'placeLimitOrderBuy'
            },
            {
                icon: undefined,
                title: 'Place limit order to sell',
                category: 'Trading',
                shortcutName: 'placeLimitOrderSell'
            }
        ]
    },
    Alerts: {
        categoryIcon: <AlarmIcon />,
        shortcuts: [
            {
                icon: <AddAlertIcon />,
                title: 'Add Alert',
                category: 'ALERT_DIALOG',
                shortcutName: 'openAlertDialog'
            }
        ]
    },
    Screener: {
        categoryIcon: <Screener sx={{ fontSize: '40px' }} />,
        shortcuts: [
            {
                icon: undefined,
                title: 'Create new screen',
                category: 'SCREENER',
                shortcutName: 'createNewScreen'
            },
            {
                icon: undefined,
                title: 'Save screen as',
                category: 'SCREENER',
                shortcutName: 'saveAsScreen'
            },
            {
                icon: undefined,
                title: 'Save screen',
                category: 'SCREENER',
                shortcutName: 'saveScreen'
            },
            {
                icon: undefined,
                title: 'Discard changes',
                category: 'SCREENER',
                shortcutName: 'discardScreenChanges'
            },
            {
                icon: undefined,
                title: 'Export screen',
                category: 'SCREENER',
                shortcutName: 'exportScreen'
            },
            {
                icon: undefined,
                title: 'Rename screen',
                category: 'SCREENER',
                shortcutName: 'renameScreen'
            }
        ]
    }
}


export function KeyboardShortcutsCategoryView(props: KeyboardShortcutsCategoryViewProps) {
    const [searchText, setSearchText] = useState('')
    const [filteredResults, setFilteredResults] = useState<any>([])

    useEffect(() => {
        if (!searchText) return

        const filteredResults: Record<ShortcutCategory, ShortcutData[]> = {
            Chart: [],
            Trading: [],
            Alerts: [],
            Screener: []
        }

        for (const category in shortcutsData) {
            const shortcuts = shortcutsData[category as ShortcutCategory].shortcuts
            const matches = shortcuts.filter((item) =>
                item.title.toLowerCase().includes(searchText.toLowerCase())
            )
            if (matches.length > 0) {
                filteredResults[category as ShortcutCategory] = matches
            }
        }
        setFilteredResults(filteredResults)
    }, [searchText])

    return (
        <Grid container sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Stack gap={2} alignItems={'center'} direction={'row'}>
                <IconButton
                    size="small"
                    sx={{ marginLeft: '-44px' }}
                    onClick={() => {
                        props.onBackClick()
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Stack gap={2}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link
                            color="inherit"
                            href="#"
                            underline="none"
                            fontSize={14}
                            onClick={() => {
                                props.onBackClick()
                            }}
                        >
                            Knowledge base
                        </Link>
                        <Typography fontSize={14} color="textPrimary">
                            Shortcuts
                        </Typography>
                    </Breadcrumbs>
                    <Typography variant="h5"
                                style={{ fontWeight: '500' }}>Keyboard Shortcuts</Typography>
                </Stack>
            </Stack>
            <TextField
                variant="standard"
                fullWidth
                autoComplete="undefined"
                sx={styles.searchInput}
                placeholder="Find shortcut"
                value={searchText}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                    disableUnderline: true
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchText(event.target.value)
                }}
            />
            <div>
                {searchText ? (
                    Object.keys(filteredResults).map((category) =>
                        filteredResults[category as ShortcutCategory].length > 0 ? (
                            <div key={category}>
                                <Typography color={'textSecondary'} variant="body2" gutterBottom>
                                    {category.toUpperCase()}
                                </Typography>
                                {filteredResults[category as ShortcutCategory].map((item: ShortcutData, index: number) => (
                                    <Stack
                                        key={index}
                                        direction={'row'}
                                        justifyContent={'space-between'}
                                        alignItems={'center'}
                                        marginBottom={2}
                                    >
                                        <Stack gap={2} direction={'row'} alignItems={'center'}>
                                            {item.icon ? <Box padding={'8px'}>
                                                {item.icon}
                                            </Box> : <Box height={'40px'} width={'40px'}></Box>}
                                            <Typography fontSize={'body1'}>{item.title}</Typography>
                                        </Stack>
                                        <ShortcutRenderer shortcutName={item.shortcutName} />
                                    </Stack>
                                ))}
                            </div>
                        ) : null
                    )
                ) : (
                    Object.keys(shortcutsData).map((category) => (
                        <Accordion sx={{
                            backgroundColor: 'none !important',
                            backgroundImage: 'none !important'
                        }} disableGutters={true} key={category}>
                            <AccordionSummary sx={{
                                '&:hover': {
                                    backgroundColor: theme => theme.palette.divider
                                },
                                '&.Mui-expanded': {
                                    backgroundColor: theme => theme.palette.divider
                                }
                            }} expandIcon={<ExpandMoreIcon />}>
                                <Stack alignItems={'center'} gap={2} direction={'row'}>
                                    {shortcutsData[category as ShortcutCategory].categoryIcon}
                                    <Typography fontSize={'18px'}>{category}</Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                {shortcutsData[category as ShortcutCategory].shortcuts.map((item, index) => (
                                    <Stack
                                        key={index}
                                        direction={'row'}
                                        justifyContent={'space-between'}
                                        alignItems={'center'}
                                        marginBottom={2}
                                    >
                                        <Stack gap={2} direction={'row'} alignItems={'center'}>
                                            {item.icon ? <Box padding={'8px'}>
                                                {item.icon}
                                            </Box> : <Box height={'40px'} width={'40px'}></Box>}
                                            <Typography fontSize={'body1'}>{item.title}</Typography>
                                        </Stack>
                                        <ShortcutRenderer shortcutName={item.shortcutName} />
                                    </Stack>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    ))
                )}
            </div>
        </Grid>
    )
}